import React, { useState, useEffect, useCallback } from 'react';
import { 
  CreditCard, 
  Plus, 
  Trash2, 
  Check, 
  Loader2, 
  AlertCircle,
  X
} from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { stripeApi } from '../../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Card brand icons/colors
const CARD_BRANDS = {
  visa: { color: 'text-blue-600', bg: 'bg-blue-50' },
  mastercard: { color: 'text-orange-600', bg: 'bg-orange-50' },
  amex: { color: 'text-blue-500', bg: 'bg-blue-50' },
  discover: { color: 'text-orange-500', bg: 'bg-orange-50' },
  default: { color: 'text-gray-600', bg: 'bg-gray-50' }
};

// Inner form component that uses Stripe hooks
const AddCardForm = ({ onSuccess, onCancel, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (stripeError) {
        setError(stripeError.message);
        setLoading(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        toast.success('Card added successfully');
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Failed to add card');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className={cn("p-6 rounded-lg border", colors.bgTertiary, colors.borderPrimary)}>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '18px',
                color: '#424770',
                lineHeight: '28px',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      
      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || loading}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Adding...
            </>
          ) : (
            'Add Card'
          )}
        </Button>
      </div>
    </form>
  );
};

const PaymentMethodManagement = ({ refreshKey }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultMethodId, setDefaultMethodId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingDefault, setSettingDefault] = useState(null);
  const [deletingMethod, setDeletingMethod] = useState(null);
  
  // Add card modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingSetup, setLoadingSetup] = useState(false);
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);

  const fetchPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      const response = await stripeApi.getPaymentMethods();
      setPaymentMethods(response.data?.payment_methods || []);
      setDefaultMethodId(response.data?.default_payment_method || null);
    } catch (err) {
      console.error('Failed to load payment methods:', err);
      toast.error('Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods, refreshKey]);

  const handleSetDefault = async (paymentMethodId) => {
    if (paymentMethodId === defaultMethodId) return;
    
    setSettingDefault(paymentMethodId);
    try {
      await stripeApi.setDefaultPaymentMethod(paymentMethodId);
      setDefaultMethodId(paymentMethodId);
      // Update local state
      setPaymentMethods(prev => prev.map(pm => ({
        ...pm,
        is_default: pm.id === paymentMethodId
      })));
      toast.success('Default payment method updated');
    } catch (err) {
      console.error('Failed to set default:', err);
      toast.error(err.response?.data?.detail || 'Failed to update default payment method');
    } finally {
      setSettingDefault(null);
    }
  };

  const handleOpenAddModal = async () => {
    setLoadingSetup(true);
    setShowAddModal(true);
    
    try {
      const response = await stripeApi.createSetupIntent();
      const { client_secret, publishable_key } = response.data;
      
      if (!publishable_key) {
        throw new Error('Stripe publishable key not configured');
      }
      
      setClientSecret(client_secret);
      setStripePromise(loadStripe(publishable_key));
    } catch (err) {
      console.error('Failed to create setup intent:', err);
      toast.error(err.response?.data?.detail || 'Failed to initialize card setup');
      setShowAddModal(false);
    } finally {
      setLoadingSetup(false);
    }
  };

  const handleAddSuccess = () => {
    setShowAddModal(false);
    setClientSecret(null);
    fetchPaymentMethods();
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setClientSecret(null);
  };

  const openDeleteConfirm = (method) => {
    setMethodToDelete(method);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!methodToDelete) return;
    
    setDeletingMethod(methodToDelete.id);
    setDeleteConfirmOpen(false);
    
    try {
      await stripeApi.deletePaymentMethod(methodToDelete.id);
      setPaymentMethods(prev => prev.filter(pm => pm.id !== methodToDelete.id));
      toast.success('Payment method removed');
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error(err.response?.data?.detail || 'Failed to remove payment method');
    } finally {
      setDeletingMethod(null);
      setMethodToDelete(null);
    }
  };

  const getBrandStyle = (brand) => {
    return CARD_BRANDS[brand?.toLowerCase()] || CARD_BRANDS.default;
  };

  const formatExpiry = (month, year) => {
    if (!month || !year) return '';
    return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
  };

  return (
    <>
      <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)} data-testid="payment-methods-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className={cn("text-lg font-semibold flex items-center gap-2", colors.textPrimary)}>
            <CreditCard className="w-5 h-5" />
            Payment Methods
          </h3>
          <Button
            onClick={handleOpenAddModal}
            variant="outline"
            size="sm"
            className={cn("border", colors.borderPrimary)}
            data-testid="add-payment-method-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Card
          </Button>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : paymentMethods.length === 0 ? (
          <div className={cn("text-center py-8", colors.textMuted)}>
            <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No payment methods on file</p>
            <p className="text-sm mt-1">Add a card to manage your subscription</p>
          </div>
        ) : (
          <div className="space-y-3">
            {paymentMethods.map((method) => {
              const brandStyle = getBrandStyle(method.brand);
              const isDefault = method.id === defaultMethodId;
              const isSettingThis = settingDefault === method.id;
              const isDeletingThis = deletingMethod === method.id;
              
              return (
                <div
                  key={method.id}
                  className={cn(
                    "flex items-center justify-between p-4 rounded-lg border transition-all",
                    isDefault ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" : colors.bgTertiary,
                    colors.borderPrimary
                  )}
                  data-testid={`payment-method-${method.id}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Card icon */}
                    <div className={cn("w-12 h-8 rounded flex items-center justify-center", brandStyle.bg)}>
                      <CreditCard className={cn("w-6 h-6", brandStyle.color)} />
                    </div>
                    
                    {/* Card details */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={cn("font-medium capitalize", colors.textPrimary)}>
                          {method.brand || 'Card'}
                        </span>
                        <span className={colors.textSecondary}>
                          •••• {method.last4}
                        </span>
                        {isDefault && (
                          <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                            Default
                          </Badge>
                        )}
                      </div>
                      <span className={cn("text-sm", colors.textMuted)}>
                        Expires {formatExpiry(method.exp_month, method.exp_year)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {!isDefault && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(method.id)}
                          disabled={isSettingThis}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          data-testid={`set-default-${method.id}`}
                        >
                          {isSettingThis ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Set Default
                            </>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteConfirm(method)}
                          disabled={isDeletingThis}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-${method.id}`}
                        >
                          {isDeletingThis ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <p className={cn("text-xs mt-4", colors.textMuted)}>
          Your default payment method will be used for subscription renewals and add-ons.
        </p>
      </Card>

      {/* Add Card Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className={cn("sm:max-w-3xl", colors.bgCard)}>
          <DialogHeader>
            <DialogTitle className={colors.textPrimary}>Add Payment Method</DialogTitle>
            <DialogDescription className={colors.textMuted}>
              Enter your card details below. Your card will be saved securely with Stripe.
            </DialogDescription>
          </DialogHeader>
          
          {loadingSetup ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className={cn("ml-2", colors.textMuted)}>Setting up secure form...</span>
            </div>
          ) : stripePromise && clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <AddCardForm
                clientSecret={clientSecret}
                onSuccess={handleAddSuccess}
                onCancel={handleCloseAddModal}
              />
            </Elements>
          ) : (
            <div className="flex items-center justify-center py-8 text-red-500">
              <AlertCircle className="w-5 h-5 mr-2" />
              Failed to load card form
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Payment Method?</AlertDialogTitle>
            <AlertDialogDescription>
              {methodToDelete && (
                <>
                  Are you sure you want to remove the {methodToDelete.brand} card ending in {methodToDelete.last4}?
                  {methodToDelete.id === defaultMethodId && (
                    <span className="block mt-2 text-amber-600 font-medium">
                      This is your default payment method. If you have an active subscription, you'll need to add a new card.
                    </span>
                  )}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PaymentMethodManagement;
